import React from 'react';
import useAppStore from '../store/useAppStore';
import './ResultPage.css';

/**
 * 推荐结果展示页面
 * 分三个阶段展示推荐流程
 */
const ResultPage = () => {
  const recommendationResult = useAppStore((state) => state.recommendationResult);
  const startMemoryUpdate = useAppStore((state) => state.startMemoryUpdate);
  const reset = useAppStore((state) => state.reset);


  if (!recommendationResult) {
    return (
      <div className="result-page">
        <div className="error-message">No recommendation result data available</div>
      </div>
    );
  }

  const {
    item_title,
    rationale,
    raw_neighbors,
    synthesized_facets,
    user_history,
    score,
    rerank_scores,
    item_id,
    user_id,
  } = recommendationResult;
  
  // 获取用户头像路径
  const baseUrl = import.meta.env.BASE_URL || '/demo/';
  const portraitPath = `${baseUrl}portrait/user${user_id}.png`;

  // 计算 Item Neighbors 的相对强度（用于条形图显示）
  const getItemNeighborRanks = () => {
    if (!raw_neighbors?.item_neighbors?.length) return {};
    // 按分数降序排序
    const sorted = [...raw_neighbors.item_neighbors].sort((a, b) => b.score - a.score);
    const maxScore = sorted[0]?.score || 1;
    
    const ranks = {};
    sorted.forEach((item, idx) => {
      ranks[item.id] = {
        rank: idx + 1,
        relativeStrength: maxScore > 0 ? (item.score / maxScore) : 1.0, // 相对于最高分的比例
        originalScore: item.score
      };
    });
    return ranks;
  };

  // 计算 User Neighbors 的相对强度（用于条形图显示）
  const getUserNeighborRanks = () => {
    if (!raw_neighbors?.user_neighbors?.length) return {};
    // 按分数降序排序
    const sorted = [...raw_neighbors.user_neighbors].sort((a, b) => b.overlap_score - a.overlap_score);
    const maxScore = sorted[0]?.overlap_score || 1;
    
    const ranks = {};
    sorted.forEach((user, idx) => {
      ranks[user.id] = {
        rank: idx + 1,
        relativeStrength: maxScore > 0 ? (user.overlap_score / maxScore) : 1.0, // 相对于最高分的比例
        originalScore: user.overlap_score
      };
    });
    return ranks;
  };

  const itemRanks = getItemNeighborRanks();
  const userRanks = getUserNeighborRanks();

  return (
    <div className="result-page">
      <div className="result-container">
        {/* Page Header */}
        <header className="result-header">
          <div className="result-header-content">
            <h1 className="result-title">
              <span className="title-icon">🎯</span>
              MemRec Recommendation Process Visualization
            </h1>
            <button className="btn-back" onClick={reset}>
              ← Back to User Selection
            </button>
          </div>
        </header>

        {/* User History */}
        <section className="user-context-section">
          <div className="user-context-header">
            <div className="user-context-avatar">
              <img 
                src={portraitPath} 
                alt="User"
                className="context-avatar-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <h3>User Interaction History</h3>
          </div>
          <div className="user-history-box">
            {user_history}
          </div>
        </section>

        {/* 流程可视化 */}
        <div className="stages-flow">
          
          {/* Stage R - Input Phase */}
          <section className="stage-section stage-input">
            <div className="stage-header">
              <span className="stage-number">1</span>
              <h2>Stage R: Noisy Neighbor Memory Input</h2>
            </div>
            
            <div className="stage-content chaotic">
              <div className="neighbors-grid">
                {/* Item Neighbors */}
                <div className="neighbor-group">
                  <h4 className="neighbor-title">📚 Neighbor Item Memories</h4>
                  <div className="neighbor-list chaotic-list">
                    {raw_neighbors?.item_neighbors
                      .sort((a, b) => b.score - a.score) // 按分数排序
                      .map((item, idx) => {
                        const rankInfo = itemRanks[item.id] || { rank: idx + 1, relativeStrength: 1.0, originalScore: item.score };
                        
                        // 解码 HTML 实体的辅助函数
                        const decodeHTML = (text) => {
                          const textarea = document.createElement('textarea');
                          textarea.innerHTML = text;
                          return textarea.value;
                        };
                        
                        // 处理描述：可能是字符串、数组或字符串形式的数组
                        let cleanDescription = '';
                        if (item.description) {
                          if (Array.isArray(item.description)) {
                            // 如果是真正的数组，合并所有非空元素
                            cleanDescription = decodeHTML(
                              item.description
                                .filter(desc => desc && desc.trim())
                                .join(' ')
                                .replace(/<[^>]*>/g, '') // 移除 HTML 标签
                                .replace(/\s+/g, ' ')    // 合并多个空格
                                .trim()
                            );
                          } else if (typeof item.description === 'string') {
                            // 检查是否是字符串形式的数组 (如 "['xxx', 'xxx']" 或被截断的 "['xxx...")
                            const trimmed = item.description.trim();
                            if (trimmed.startsWith('[')) {
                              // 可能是数组格式，尝试清理
                              try {
                                // 如果有完整的结尾 ]，尝试 JSON 解析
                                if (trimmed.endsWith(']')) {
                                  const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
                                if (Array.isArray(parsed)) {
                                  cleanDescription = decodeHTML(
                                    parsed
                                      .filter(desc => desc && desc.trim())
                                      .join(' ')
                                      .replace(/<[^>]*>/g, '')
                                      .replace(/\s+/g, ' ')
                                      .trim()
                                  );
                                  } else {
                                    throw new Error('Not an array');
                                  }
                                } else {
                                  throw new Error('Incomplete array');
                                }
                              } catch (e) {
                                // 解析失败或不完整，手动清理数组格式
                                cleanDescription = decodeHTML(
                                  trimmed
                                    .replace(/^\[\s*['"]?/, '')  // 移除开头的 [ 和可能的引号
                                    .replace(/['"]?\s*\]?$/, '') // 移除结尾的引号和可能的 ]
                                    .replace(/['"]\s*,\s*['"]/g, ' ') // 将 ', ' 替换为空格
                                    .replace(/['"]/g, '')        // 移除剩余的引号
                                    .replace(/<[^>]*>/g, '')     // 移除 HTML 标签
                                    .replace(/\s+/g, ' ')        // 合并多个空格
                                    .trim()
                                );
                              }
                            } else {
                              // 普通字符串
                              cleanDescription = decodeHTML(
                                item.description
                                  .replace(/<[^>]*>/g, '')
                                  .replace(/\s+/g, ' ')
                                  .trim()
                              );
                            }
                          }
                        }
                        
                        // 解码标题中的 HTML 实体
                        const decodedTitle = decodeHTML(item.title);
                        
                        // Summary: 如果没有描述或描述为 'Nan'，使用标题作为描述
                        let summary;
                        if (cleanDescription && cleanDescription.toLowerCase() !== 'nan') {
                          summary = `${cleanDescription.substring(0, 100)}${cleanDescription.length > 100 ? '...' : ''}`;
                        } else {
                          // 使用标题作为备用描述，生成自然的描述文本
                          const truncatedTitle = decodedTitle.length > 65 ? decodedTitle.substring(0, 65) + '...' : decodedTitle;
                          // 根据 item id 使用不同的模板，让显示更自然
                          const templates = [
                            `This neighbor enjoyed "${truncatedTitle}".`,
                            `"${truncatedTitle}" is a book in this user's reading history.`,
                            `A recommended item: "${truncatedTitle}".`,
                            `Book title: "${truncatedTitle}".`
                          ];
                          summary = templates[item.id % templates.length];
                        }
                        
                        return (
                          <div 
                            key={item.id} 
                            className="neighbor-item chaotic-item"
                          >
                            <div className="neighbor-rank-badge">#{rankInfo.rank}</div>
                            <div className="neighbor-content">
                              <div className="neighbor-header">
                                <span className="neighbor-id">Item-{item.id}</span>
                                <span className="neighbor-title-inline" title={decodedTitle}>
                                  {decodedTitle.length > 50 ? decodedTitle.substring(0, 50) + '...' : decodedTitle}
                                </span>
                                <span className="neighbor-score-tooltip" title={`Original score: ${rankInfo.originalScore.toFixed(3)}`}>
                                  {(rankInfo.relativeStrength * 100).toFixed(0)}%
                                </span>
                              </div>
                              <div className="neighbor-strength-bar">
                                <div 
                                  className="strength-bar-fill" 
                                  style={{ width: `${rankInfo.relativeStrength * 100}%` }}
                                ></div>
                              </div>
                              <div className="neighbor-text">
                                <div className="neighbor-summary">
                                  {summary}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* User Neighbors */}
                <div className="neighbor-group">
                  <h4 className="neighbor-title">👥 Neighbor User Memories</h4>
                  <div className="neighbor-list chaotic-list">
                    {raw_neighbors?.user_neighbors
                      .sort((a, b) => b.overlap_score - a.overlap_score) // 按分数排序
                      .map((user, idx) => {
                        const rankInfo = userRanks[user.id] || { rank: idx + 1, relativeStrength: 1.0, originalScore: user.overlap_score };
                        
                        // 解码 HTML 实体的辅助函数
                        const decodeHTML = (text) => {
                          const textarea = document.createElement('textarea');
                          textarea.innerHTML = text;
                          return textarea.value;
                        };
                        
                        // 分割 items 列表并解码 HTML 实体
                        const itemsList = user.recent_items
                          .split(',')
                          .map(item => decodeHTML(item.trim()))
                          .filter(item => item);
                        
                        // 标签：显示第一个 item 的缩写
                        const firstItem = itemsList[0] || 'No items';
                        const labelText = firstItem.length > 35 ? firstItem.substring(0, 35) + '...' : firstItem;
                        
                        // Summary: 显示前 2-3 个 items，最多100字符，统一加...
                        const summaryItems = itemsList.slice(0, 3).join(', ');
                        const summary = summaryItems.length > 100 
                          ? summaryItems.substring(0, 100) + '...'
                          : summaryItems + '...';
                        
                        return (
                          <div 
                            key={user.id} 
                            className="neighbor-item chaotic-item"
                          >
                            <div className="neighbor-rank-badge">#{rankInfo.rank}</div>
                            <div className="neighbor-content">
                              <div className="neighbor-header">
                                <span className="neighbor-id">User-{user.id}</span>
                                <span className="neighbor-label-inline" title={`${itemsList.length} items: ${itemsList.slice(0, 5).join(', ')}${itemsList.length > 5 ? '...' : ''}`}>
                                  {labelText}
                                </span>
                                <span className="neighbor-score-tooltip" title={`Overlap score: ${(rankInfo.originalScore * 100).toFixed(1)}%`}>
                                  {(rankInfo.relativeStrength * 100).toFixed(0)}%
                                </span>
                              </div>
                              <div className="neighbor-strength-bar">
                                <div 
                                  className="strength-bar-fill" 
                                  style={{ width: `${rankInfo.relativeStrength * 100}%` }}
                                ></div>
                              </div>
                              <div className="neighbor-text">
                                <div className="neighbor-summary">
                                  {summary}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Flow Arrow */}
          <div className="flow-arrow">
            <div className="arrow-icon">⬇</div>
            <div className="arrow-text">LLM Collaborative Denoising & Synthesis</div>
          </div>

          {/* Stage R - Synthesis Phase */}
          <section className="stage-section stage-synthesis">
            <div className="stage-header">
              <span className="stage-number">2</span>
              <h2>Stage R: Collaborative Memory Synthesis</h2>
            </div>
            
            <div className="stage-content">
              <div className="facets-grid">
                {synthesized_facets?.map((facet, idx) => (
                  <div key={idx} className="facet-card">
                    <div className="facet-header">
                      <span className="facet-icon">✨</span>
                      <span className="facet-confidence">
                        Confidence: {(facet.confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                    <p className="facet-description">{facet.facet}</p>
                    <div className="facet-support">
                      <small>
                        Supporting Neighbors ({facet.supporting_neighbors?.length || 0}):{' '}
                        {facet.supporting_neighbors && facet.supporting_neighbors.length > 0 ? (
                          <span className="supporting-neighbors-list">
                            {facet.supporting_neighbors.map((neighbor, nIdx) => (
                              <span key={nIdx} className="neighbor-badge">
                                {neighbor}
                                {nIdx < facet.supporting_neighbors.length - 1 && ', '}
                              </span>
                            ))}
                          </span>
                        ) : (
                          'None'
                        )}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Flow Arrow */}
          <div className="flow-arrow">
            <div className="arrow-icon">⬇</div>
            <div className="arrow-text">Re-ranking Based on Facets</div>
          </div>

          {/* Stage ReRank - Output Phase */}
          <section className="stage-section stage-output">
            <div className="stage-header">
              <span className="stage-number">3</span>
              <h2>Stage ReRank: Final Recommendation Result</h2>
            </div>
            
            <div className="stage-content">
              <div className="recommendation-card">
                <div className="recommendation-header">
                  <h3 className="recommended-title">
                    <span className="title-icon">🎁</span>
                    {item_title}
                  </h3>
                  <div className="recommendation-score">
                    <span className="score-label">LLM Match Score</span>
                    <span className="score-value">{(score * 100).toFixed(0)}%</span>
                  </div>
                </div>
              
              <div className="rationale-section">
                <h4 className="rationale-title">💡 Recommendation Rationale</h4>
                <blockquote className="rationale-text">
                  {rationale}
                </blockquote>
              </div>
            </div>
          </div>

          {/* Complete ReRank List */}
          {rerank_scores && rerank_scores.length > 0 && (
            <div className="rerank-list-section">
              <h4 className="rerank-list-title">📋 Complete ReRank List (Sorted by LLM Score)</h4>
              <div className="rerank-list">
                {rerank_scores.map((item, idx) => (
                  <div 
                    key={item.item_id} 
                    className={`rerank-item ${item.is_recommended ? 'recommended' : ''}`}
                  >
                    <div className="rerank-rank">#{idx + 1}</div>
                    <div className="rerank-content">
                      <div className="rerank-header">
                        <span className="rerank-item-id">Item-{item.item_id}</span>
                        <span className="rerank-score">{(item.score * 100).toFixed(0)}%</span>
                        {item.is_recommended && (
                          <span className="recommended-badge">⭐ Recommended</span>
                        )}
                      </div>
                      {item.rationale && (
                        <div className="rerank-rationale">{item.rationale}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
        </div>

        {/* Next Step Action */}
        <footer className="result-footer">
          <button className="btn-next-stage" onClick={startMemoryUpdate}>
            <span className="btn-icon">👍</span>
            <span className="btn-text">Simulate User Click/Like This Item</span>
            <span className="btn-subtitle">(Enter Stage W - Memory Update)</span>
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ResultPage;

